import { useEffect, useState } from "react";
import api from "../axios";
import { ShoppingBag, CheckCircle2, XCircle, Trash2, ImageOff } from "lucide-react";

const MEDIA_BASE = (process.env.REACT_APP_API_URL || "http://localhost:5000/api").replace(
  "/api",
  ""
);

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-700",
  confirmed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const res = await api.get("/orders");
      setOrders(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const updateStatus = async (id, status) => {
    setBusyId(id);
    try {
      await api.put(`/orders/${id}`, { status });
      loadOrders();
    } catch (e) {
      alert("Update failed");
    } finally {
      setBusyId(null);
    }
  };

  const deleteOrder = async (id) => {
    if (!window.confirm("Ye order delete karna hai?")) return;
    setBusyId(id);
    try {
      await api.delete(`/orders/${id}`);
      loadOrders();
    } catch (e) {
      alert("Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  const totalRevenue = orders
    .filter((o) => o.status === "confirmed")
    .reduce((sum, o) => sum + Number(o.agreed_price || 0), 0);

  return (
    <div className="w-full min-h-full bg-gray-50 px-4 py-6 md:px-8 md:py-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ShoppingBag className="text-purple-600" size={24} />
              Orders / Bookings
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Jab AI WhatsApp par customer se deal close karta hai, order yahan automatically aa jaata hai.
            </p>
          </div>

          <div className="flex items-center gap-2 bg-white border rounded-xl px-4 py-3 shadow-sm">
            <div>
              <div className="text-[11px] text-gray-500 leading-none">Total Confirmed Sales</div>
              <div className="font-bold text-gray-900">₹{totalRevenue.toFixed(2)}</div>
            </div>
          </div>
        </div>

        {loading ? (
          <p className="text-gray-500 text-sm">Loading...</p>
        ) : !orders.length ? (
          <p className="text-gray-500 text-sm">Abhi tak koi order/sale nahi hui.</p>
        ) : (
          <div className="space-y-3 pb-8">
            {orders.map((o) => {
              const isOpen = expandedId === o.id;
              return (
                <div key={o.id} className="bg-white border rounded-2xl shadow-sm overflow-hidden">
                  <div className="p-4 flex flex-wrap gap-4 items-center">
                    {o.product_image ? (
                      <img
                        src={`${MEDIA_BASE}${o.product_image}`}
                        alt=""
                        className="w-14 h-14 rounded-lg object-cover border flex-shrink-0"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <ImageOff size={18} className="text-gray-400" />
                      </div>
                    )}

                    <div className="flex-1 min-w-[220px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">
                          {o.customer_name || "Unknown Customer"}
                        </span>
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLES[o.status] || "bg-gray-100 text-gray-700"}`}>
                          {o.status}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {o.product_name} • ₹{o.agreed_price}
                        {o.sale_price && Number(o.sale_price) !== Number(o.agreed_price) && (
                          <span className="text-gray-400"> (asking price was ₹{o.sale_price})</span>
                        )}
                        {" • "}
                        {o.phone}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      {o.status !== "confirmed" && (
                        <button
                          onClick={() => updateStatus(o.id, "confirmed")}
                          disabled={busyId === o.id}
                          className="text-xs font-semibold bg-green-600 text-white px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50"
                        >
                          <CheckCircle2 size={13} /> Confirm
                        </button>
                      )}
                      {o.status !== "cancelled" && (
                        <button
                          onClick={() => updateStatus(o.id, "cancelled")}
                          disabled={busyId === o.id}
                          className="text-xs font-semibold bg-amber-600 text-white px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50"
                        >
                          <XCircle size={13} /> Cancel
                        </button>
                      )}
                      <button
                        onClick={() => setExpandedId(isOpen ? null : o.id)}
                        className="text-xs text-gray-600 border px-2.5 py-1.5 rounded-lg"
                      >
                        Details
                      </button>
                      <button
                        onClick={() => deleteOrder(o.id)}
                        disabled={busyId === o.id}
                        className="text-xs text-red-600 border border-red-200 px-2 py-1.5 rounded-lg"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="border-t bg-gray-50 p-4 text-sm space-y-1.5">
                      <p><span className="font-semibold">Phone:</span> {o.phone}</p>
                      <p><span className="font-semibold">Product/Service:</span> {o.product_name}</p>
                      <p><span className="font-semibold">Agreed Price:</span> ₹{o.agreed_price}</p>
                      <p><span className="font-semibold">Order Date:</span> {new Date(o.created_at).toLocaleString()}</p>
                      {o.chat_snippet && (
                        <p><span className="font-semibold">Last Chat Message:</span> {o.chat_snippet}</p>
                      )}
                      {o.notes && <p><span className="font-semibold">Notes:</span> {o.notes}</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}